import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import { Info, X } from 'lucide-react'
import {
  CUSTOM_ARM,
  generatedVersionsLocked,
  orderVariantsForDisplay,
  shouldShowVariantToggle,
  VARIANT_TOGGLE_EXPLAINER,
  variantLabel,
} from '../lib/message-variants'
import type { OutreachMessageVariant } from '../types'

/**
 * Which stored version of a message is on screen.
 *
 * A field can have several: the control (the base prompt), one per A/B arm, and
 * the founder's own edit. Sits beside Edit because it changes what you SEE,
 * while Edit changes what you SEND — keeping them apart matters, since editing
 * any version saves it as the custom one and stops the A/B for that message.
 *
 * Hidden entirely when there is nothing to choose between, so a startup with a
 * single version sees exactly the dialog it saw before.
 */
export function VariantToggle({
  variants,
  selected,
  onSelect,
  onDropCustom,
}: {
  variants: OutreachMessageVariant[]
  selected: string
  onSelect: (arm: string) => void
  /** Discard the founder's edit and go back to the generated copy. */
  onDropCustom?: () => void
}) {
  if (!shouldShowVariantToggle(variants)) return null

  // The generated versions stay VISIBLE while an edit exists, so it is obvious
  // the alternatives are still there — behind the X.
  const pinned = generatedVersionsLocked(variants)

  return (
    <span className="inline-flex items-center gap-1">
      <fieldset
        className="inline-flex items-stretch overflow-hidden rounded-md border border-border/60"
        aria-label="Message version"
      >
        {orderVariantsForDisplay(variants).map((variant) => {
          const isCustom = variant.arm === CUSTOM_ARM
          const disabled = pinned && !isCustom
          return (
            <span key={variant.id} className="inline-flex items-stretch">
              <button
                type="button"
                onClick={() => onSelect(variant.arm)}
                disabled={disabled}
                aria-pressed={variant.arm === selected}
                title={disabled ? 'Your own version is being sent for this message' : undefined}
                data-testid={`variant-toggle-${variant.arm}`}
                className={cn(
                  'px-1.5 py-0.5 text-[10.5px] font-medium transition-colors',
                  variant.arm === selected
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                  disabled && 'cursor-not-allowed opacity-40 hover:text-muted-foreground'
                )}
              >
                {variantLabel(variant.arm)}
              </button>
              {isCustom && onDropCustom && (
                <button
                  type="button"
                  onClick={onDropCustom}
                  aria-label="Discard your version and use the generated one"
                  title="Discard your version and use the generated one"
                  data-testid="variant-toggle-drop-custom"
                  className={cn(
                    'pr-1.5 py-0.5 transition-colors',
                    variant.arm === selected
                      ? 'bg-primary/15 text-primary hover:text-primary/70'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <X className="size-3" strokeWidth={2.5} />
                </button>
              )}
            </span>
          )
        })}
      </fieldset>
      {/* Several versions of a message the founder is about to send under their
          own name needs explaining unprompted — without it the switcher reads
          as "we could not decide". */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Why there are several versions"
            data-testid="variant-toggle-info"
            className="text-muted-foreground/70 transition-colors hover:text-foreground"
          >
            <Info className="size-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-[260px] bg-foreground text-[11.5px] leading-relaxed text-background"
        >
          {VARIANT_TOGGLE_EXPLAINER}
        </TooltipContent>
      </Tooltip>
    </span>
  )
}
